import { useState } from "react";
import { Helmet } from "react-helmet-async";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  AutoFillStatus,
  IntakeForm,
  LoadResponses,
  useAnalytics,
  Toast,
  FieldItem,
} from "@smart/bridge-intake-components-dom";
import { track } from "@smart/bridge-metrics-dom";
import { StaffDetails } from "@smart/bridge-types-basic/src/appointment";
import { useAsync } from "@smart/itops-hooks-dom";
import { Modal } from "@smart/itops-sb-design-system-dom";
import { delay, inIframe } from "@smart/itops-utils-basic";

import {
  intakeApi,
  IntakeApiAIUserFeedback,
  IntakeApiForm,
  IntakeApiMatter,
  IntakeApiSubmission,
  IntakeApiTeam,
  useAddressLookup,
  useFileLookup,
} from "../../hooks";

const AI_FILL_POLLING_INTERVAL = 2000;

export type SubmissionFormProps = {
  matter?: IntakeApiMatter;
  team: IntakeApiTeam;
  form: IntakeApiForm;
  submission: IntakeApiSubmission;
  aiUserFeedback: IntakeApiAIUserFeedback | undefined;
  autoFillStatus?: AutoFillStatus;
  setAutoFillStatus: (status: AutoFillStatus | undefined) => void;
  hideTeam?: boolean;
  loadResponses: LoadResponses;
  staffDetails: StaffDetails[];
  reloadSubmission: () => void;
  featureFlagUserIdentified: boolean;
  captchaToken?: string;
  setToasts: (toasts: Toast[]) => void;
  setFields: (setFn: (fields: FieldItem[]) => FieldItem[]) => void;
};

export const SubmissionForm = ({
  matter,
  team,
  form,
  submission,
  aiUserFeedback,
  autoFillStatus,
  setAutoFillStatus,
  hideTeam,
  loadResponses,
  staffDetails,
  reloadSubmission,
  featureFlagUserIdentified,
  captchaToken,
  setToasts,
  setFields,
}: SubmissionFormProps) => {
  const showConnectionBanner = useState(!inIframe());
  const uris = {
    teamUri: team.uri,
    submissionUri: submission.uri,
    formUri: form.uri,
  };
  const lookup = {
    addressLookup: useAddressLookup(),
    fileLookup: useFileLookup(uris),
  };
  const { post, result, ...connection } = intakeApi.submit();
  const { sendEvent } = useAnalytics();
  const { leadPro, matterFlow } = useFeatureFlags();

  useAsync(async () => {
    if (!autoFillStatus && aiUserFeedback?.status === "generating") {
      setAutoFillStatus("filling");
      return;
    }
    if (autoFillStatus !== "filling" || aiUserFeedback?.status === "reviewed") {
      return;
    }

    if (aiUserFeedback?.status && aiUserFeedback.status !== "generating") {
      setAutoFillStatus("completed");
    } else {
      await delay(AI_FILL_POLLING_INTERVAL);
      reloadSubmission();
    }
  }, [autoFillStatus, aiUserFeedback, submission]);

  return (
    <>
      <Helmet>
        <title>{[team.name, form.title].filter(Boolean).join(" | ")}</title>
      </Helmet>
      <IntakeForm
        key={
          autoFillStatus === "filling"
            ? JSON.stringify(submission.responses)
            : undefined
        }
        connection={connection}
        isMatterClosed={matter?.isClosed}
        form={{
          team,
          hideTeam,
          response: form.response,
          category: form.category,
          aiFillSettings: form.aiFillSettings,
          title: form.title,
        }}
        sections={form.sections}
        groups={form.groups}
        fields={form.fields}
        lookup={lookup}
        submission={submission}
        setToasts={setToasts}
        setFields={setFields}
        submitResponses={(input) => {
          if (input.status === "completed") {
            track("Submission Completed", {
              formUri: uris.formUri,
              submissionUri: uris.submissionUri,
            });
            sendEvent({
              name: "form_submission",
              params: {
                form_title: form.title,
              },
            });
          }

          return post({ body: { ...uris, ...input, captchaToken } });
        }}
        loadResponses={loadResponses}
        shouldLoadDBResponses
        postSubmissionStatus={result?.submissionStatus}
        staffDetails={staffDetails}
        showConnectionBanner={showConnectionBanner}
        autoFillStatus={autoFillStatus}
        runAutoFilling={async (uploadedFileKeys: string[]) => {
          await post({
            body: {
              ...uris,
              responses: {},
              aiFillFiles: uploadedFileKeys,
              captchaToken,
            },
          });
          setAutoFillStatus("filling");
        }}
        resetAutoFilling={() => setAutoFillStatus(undefined)}
        submitAutofillFeedback={async (feedback) => {
          await post({
            body: {
              ...uris,
              responses: {},
              autofillFeedback: feedback,
              captchaToken,
            },
          });
        }}
      />
      <Modal
        open={
          featureFlagUserIdentified &&
          ((leadPro === false && form.category === "lead") ||
            (matterFlow === false && form.category === "matter"))
        }
        header={{
          icon: "solidCircleInfo",
          text: "Form not available",
        }}
        footer={{
          buttons: [
            {
              variant: "primaryDefault",
              text: "OK",
            },
          ],
        }}
      >
        <p>The form is no longer available. Please contact {team.name}.</p>
      </Modal>
    </>
  );
};
